import React from "react";

import { animated, useSpring, config } from "@react-spring/web";
import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { scaleLinear } from "@visx/scale";
import { Arc } from "@visx/shape";
import { TextProps } from "@visx/text";
import { ScaleLinear } from "d3-scale";

import Stack from "@rui/Stack";

import LabelText from "./LabelText";
import ValueText from "./ValueText";

interface GaugeProps {
  height: number;
  width: number;
  /** value between 0 and 100% **/
  value: number;
  /** label to show in center of donut defaults to value **/
  label?: string;
  /** a secondary label to show under the label in the in donut **/
  subLabel?: string;
  /** if set, show a marker at the given value **/
  markerValue?: number | null;
  /** arc fill color **/
  fill?: string;
  loading?: boolean;
  empty?: boolean;
  range: [number, number];
  labels?: {
    value: number;
    label?: string;
    textAnchor: TextProps["textAnchor"];
  }[];
  scaleColor: ScaleLinear<React.CSSProperties["fill"], string>;
}

type GaugeParentProps = Omit<GaugeProps, "width">;

const TEXT_SPACING_DEFAULT = 24;
const TEXT_SPACING_SMALL = 18;
const MARGIN = 30;
const WIDTH_OFFSET = MARGIN * 2;
const PIE_WIDTH = 20;
const PI = Math.PI;
const START_RADIANS = PI + PI / 2;
const FULL_RADIANS = PI + (3 * PI) / 2;
const LABEL_MARGIN = 8;
const VALUE_WIDTH_LARGE_BREAKPOINT = 280;
const VALUE_WIDTH_SMALL_BREAKPOINT = 240; // below this width, just don't show a value under the gauge since it'll have to be tiny anyway
const VALUE_BOTTOM_OFFSET = {
  small: 25,
  large: 40,
};

const AnimatedArc = animated(Arc);

const Gauge = ({
  loading,
  empty,
  height,
  width,
  value,
  label,
  range,
  subLabel,
  fill,
  labels,
  scaleColor,
}: GaugeProps) => {
  const [minValue, maxValue] = range;
  const radius = height - MARGIN;
  const midRadius = radius + LABEL_MARGIN;
  const center = width / 2;
  // because of the rounded edges, low values must have a minimum bar length otherwise it'll appear the fill arc is below the base arc
  const minValueToAllowOnGauge = minValue + 3;

  const scaleValue = scaleLinear({
    domain: [minValue, maxValue],
    range: [START_RADIANS, FULL_RADIANS],
    clamp: true,
  });

  const { endAngle, startAngle } = useSpring({
    from: { endAngle: START_RADIANS, startAngle: START_RADIANS },
    to: {
      endAngle: scaleValue(
        Math.max(minValueToAllowOnGauge, value) ?? START_RADIANS
      ),
      startAngle: START_RADIANS,
    },
    config: config.slow,
  });

  const { loadingOpacity } = useSpring({
    from: { loadingOpacity: 0.5 },
    to: { loadingOpacity: 1 },
    loop: { reverse: true },
    config: config.default,
  });

  const textTop =
    height -
    (width > VALUE_WIDTH_LARGE_BREAKPOINT
      ? VALUE_BOTTOM_OFFSET.large
      : VALUE_BOTTOM_OFFSET.small);

  return (
    <svg width={width} height={height}>
      {width > VALUE_WIDTH_SMALL_BREAKPOINT &&
        (empty ? (
          <ValueText x={center} y={textTop} fontSize={14} fill="#656565">
            –
          </ValueText>
        ) : (
          <>
            {(label || value) && !loading ? (
              <ValueText
                x={center}
                y={textTop}
                fontSize={width > VALUE_WIDTH_LARGE_BREAKPOINT ? 32 : 24}
                fontWeight={600}
              >
                {label ?? `${value}`}
              </ValueText>
            ) : null}
            {subLabel && (
              <ValueText
                x={center}
                y={
                  textTop +
                  (width > VALUE_WIDTH_LARGE_BREAKPOINT
                    ? TEXT_SPACING_DEFAULT
                    : TEXT_SPACING_SMALL)
                }
                fontSize={width > VALUE_WIDTH_LARGE_BREAKPOINT ? 20 : 14}
                fill="#656565"
              >
                {subLabel}
              </ValueText>
            )}
          </>
        ))}
      <Group top={height} left={center}>
        {/* Labels */}
        {(labels ?? []).map((label) => (
          <LabelText
            key={label.value}
            textAnchor={label.textAnchor}
            x={midRadius * Math.sin(scaleValue(label.value))}
            y={
              midRadius * Math.cos(scaleValue(label.value) + PI) - LABEL_MARGIN
            }
          >
            {label.label ?? label.value}
          </LabelText>
        ))}

        {/* Base arc */}
        <AnimatedArc
          startAngle={START_RADIANS}
          endAngle={FULL_RADIANS} // in radians
          outerRadius={radius}
          innerRadius={radius - PIE_WIDTH}
          padAngle={0}
          cornerRadius={10}
          fill="#D9D9D9"
          opacity={loading ? loadingOpacity : 1}
        />
        {empty || loading ? null : (
          <>
            {/* Fill arc */}
            <AnimatedArc
              startAngle={startAngle}
              endAngle={endAngle} // in radians
              outerRadius={radius}
              innerRadius={radius - PIE_WIDTH}
              cornerRadius={10}
              fill={fill ?? scaleColor(value)}
              opacity={1}
            />
          </>
        )}
      </Group>
    </svg>
  );
};

const GaugeWrapper = ({
  height: desiredHeight,
  ...gaugeProps
}: GaugeParentProps) => (
  <ParentSize>
    {({ width: parentWidth }) => {
      const desiredSvgWidth = desiredHeight * 2 + WIDTH_OFFSET;
      const width = Math.max(0, Math.min(desiredSvgWidth, parentWidth));

      const height = Math.max(
        0,
        Math.min(desiredHeight, (width - WIDTH_OFFSET) / 2)
      );

      return (
        <Stack variant="center" sx={{ width: "100%" }}>
          <Gauge height={height} width={width} {...gaugeProps} />
        </Stack>
      );
    }}
  </ParentSize>
);

export default GaugeWrapper;
